// Un ejemplo por función del asistente para la demo de «Poderes del chat» (lienzo Poderes_Demo, `ejemplos()`).
// Las claves son los ids de catalogoFuncionesE1 y cada `pregunta` es el `ejemplo` del catálogo (un test lo exige),
// para que la demo no anuncie nada que la app no haga (D-22). No hay nombres de función aquí: la demo muestra el
// `nombre` del catálogo (C-5). Las respuestas son casos ficticios con la paciente de ejemplo.
// Los colores del tablero son nombres de tono, nunca hex: el componente los traduce a tokens.
//   tono de fila: 'ok' (azul de acento), 'neutro' (gris por defecto), 'dudosa' (ámbar sobre fondo ámbar);
//   variante de chip: 'acento' (azul), 'notasFijas' (amarillo de las notas fijas).
// Se conservan «Ha leído las escalas que puntuaste» y «Escalas puntuadas por ti» (D-29).

export type IdFuncionPoder =
    | 'preguntar_historial'
    | 'plan_terapeutico'
    | 'psicometria'
    | 'documentos_por_secciones'
    | 'plantillas_clinicas'
    | 'manuscritos'
    | 'cronologia'
    | 'carpetas'
    | 'fecha_real'
    | 'notas_fijas'
    | 'ayuda';

export type TonoFilaEjemplo = 'ok' | 'neutro' | 'dudosa';
export type VarianteChipEjemplo = 'acento' | 'notasFijas';

export interface FilaEjemplo {
    readonly etiqueta: string;
    readonly valor: string;
    readonly tono: TonoFilaEjemplo;
}

export interface ChipEjemplo {
    readonly texto: string;
    readonly variante: VarianteChipEjemplo;
}

export interface EjemploPoder {
    readonly pregunta: string;
    readonly paso: string;
    readonly nota: string;
    readonly adjunto?: string;
    readonly texto?: string;
    readonly cita?: string;
    readonly cabecera?: string;
    readonly subcabecera?: string;
    readonly filas?: readonly FilaEjemplo[];
    readonly propuesta?: string;
    readonly chip?: ChipEjemplo;
}

function fila(etiqueta: string, valor: string, tono: TonoFilaEjemplo = 'neutro'): FilaEjemplo {
    return { etiqueta, valor, tono };
}

export const EJEMPLOS_PODERES: Readonly<Record<IdFuncionPoder, EjemploPoder>> = {
    preguntar_historial: {
        pregunta: '¿Cuándo habló por primera vez del insomnio?',
        paso: 'Ha leído las 12 sesiones y 6 documentos',
        texto: 'En la primera sesión, al explicar por qué venía: tardaba más de una hora en dormirse y se despertaba de madrugada',
        cita: '[S1]',
        nota: 'Cada dato lleva su cita: tócala y abre ese momento de la sesión.',
    },
    plan_terapeutico: {
        pregunta: '¿Qué objetivos han avanzado?',
        paso: 'Ha revisado el plan y las sesiones 10 a 12',
        cabecera: 'Plan de tratamiento de Marta',
        subcabecera: 'Avance según lo que cuenta en sesión',
        filas: [
            fila('Registro de sueño diario', '14 de 14 días', 'ok'),
            fila('Rutina de sueño estable', '70 %'),
            fila('Aplazar la preocupación al acostarse', '30 %'),
        ],
        propuesta: 'Marcar como logrado «Registro de sueño diario»',
        nota: 'El plan solo cambia si tú lo apruebas.',
    },
    psicometria: {
        pregunta: 'Interpreta el GAD-7 y el BDI-II',
        paso: 'Ha leído las escalas que puntuaste',
        cabecera: 'Escalas puntuadas por ti',
        subcabecera: 'Junio y octubre de 2026',
        filas: [
            fila('GAD-7 · ansiedad generalizada', '16 → 11', 'ok'),
            fila('BDI-II · sintomatología depresiva', '22 → 16', 'ok'),
        ],
        texto: 'Las dos bajan. Pero en las dos últimas sesiones sigue contando que le cuesta dormirse: te lo señalo como discrepancia a explorar.',
        cita: '[S11–S12]',
        nota: 'Describe y compara; no puntúa ni diagnostica. La lectura clínica es tuya.',
    },
    documentos_por_secciones: {
        pregunta: 'Prepara el informe de seguimiento',
        paso: 'Ha usado lo registrado de junio a octubre',
        cabecera: 'Informe de seguimiento · borrador',
        subcabecera: 'Se redacta sección a sección',
        filas: [
            fila('1 · Motivo de consulta', 'Lista', 'ok'),
            fila('2 · Evolución', 'Lista', 'ok'),
            fila('3 · Evaluación', 'Lista', 'ok'),
            fila('4 · Recomendaciones', 'Pendiente'),
        ],
        nota: 'Cada dato lleva su cita. Revisa cada sección antes de exportar a PDF o Word.',
    },
    plantillas_clinicas: {
        pregunta: 'Hazme el análisis funcional',
        paso: 'Ha aplicado la plantilla «Análisis funcional»',
        cabecera: 'Análisis funcional · borrador',
        subcabecera: 'Con lo que consta en las sesiones',
        filas: [
            fila('Antecedentes', 'Acostarse con el trabajo pendiente'),
            fila('Conducta', 'Repasar tareas en la cama'),
            fila('Consecuencias', 'Tarda más en dormirse'),
        ],
        nota: 'También: Beck, Young, SOAP, BIRP, DAP e historia clínica.',
    },
    manuscritos: {
        adjunto: 'notas-sesion-3.jpg',
        pregunta: 'Transcribe estas notas',
        paso: 'Ha leído la imagen adjunta',
        texto: '«Duerme mejor desde que deja el móvil fuera. Pendiente: registro de sueño 2 semanas.»',
        chip: { texto: 'Transcripción lista para revisar', variante: 'acento' },
        nota: 'También revisa cuestionarios escaneados.',
    },
    cronologia: {
        pregunta: 'Ordena todo por fecha',
        paso: 'Ha ordenado 12 sesiones y 6 documentos',
        cabecera: 'Junio – octubre de 2026',
        subcabecera: 'Cada fecha dice de dónde sale',
        filas: [
            fila('Sesión 12', '6 oct'),
            fila('Registro de sueño de julio', '21 jul'),
            fila('Notas manuscritas · sesión 3', '2 jul · dudosa', 'dudosa'),
            fila('Consentimiento informado', '16 jun'),
        ],
        nota: 'Las fechas dudosas se marcan para que las revises.',
    },
    carpetas: {
        pregunta: 'Mete los tests de marzo en una carpeta',
        paso: 'Ha encontrado 2 tests de marzo',
        cabecera: 'Carpeta «Tests de marzo»',
        subcabecera: '2 documentos',
        filas: [fila('GAD-7 · 12 mar 2026', 'Test'), fila('BDI-II · 12 mar 2026', 'Test')],
        nota: 'Agrupa sesiones, notas, documentos y tests por fecha o a tu manera.',
    },
    fecha_real: {
        pregunta: 'Revisa las fechas de los tests',
        paso: 'Ha leído la fecha escrita en cada documento',
        cabecera: 'Fechas de los tests',
        subcabecera: 'Escrita en el documento o, si no hay, la del archivo',
        filas: [
            fila('BDI-II', '6 oct · del documento', 'ok'),
            fila('GAD-7', '12 mar · del documento', 'ok'),
            fila('Cuestionario sin fecha', '2 jul · del archivo', 'dudosa'),
        ],
        nota: 'Siempre puedes cambiar cualquier fecha a mano.',
    },
    notas_fijas: {
        pregunta: 'Marcos es su hermano, no su pareja',
        paso: 'Ha guardado la corrección',
        texto: 'Anotado. Lo tendré siempre en cuenta al responderte sobre Marta.',
        chip: { texto: 'Notas fijas (1)', variante: 'notasFijas' },
        nota: 'Corriges una vez y la corrección queda fijada.',
    },
    ayuda: {
        pregunta: '¿Cómo subo notas antiguas?',
        paso: 'Ha buscado en la ayuda de TherapyRecap',
        texto: 'Desde Documentos › Subir documento. Si el documento tiene la fecha escrita, se usa esa; si no, la del archivo.',
        chip: { texto: 'Abrir Documentos ↗', variante: 'acento' },
        nota: 'Dudas de uso de la app, con enlaces directos.',
    },
};
